'use client';

import { ListHeader } from './list-header';
import { MyFavouritesLeagueButton } from '@/features/my-favourites/components/my-favourites-league-button';
import { useAppearanceStore } from '@/store/appearance';
import { siteService } from '@/services/site.service';
import { useGetSiteConfigs } from '@/hooks/query/use-site-configs';
import { leagueStyles } from '@/features/odds-table-mars/components/league/league.style';
import clsx from 'clsx';
import { useAccountInfo } from '@/hooks/query/account';
import { LeagueIcon } from '@/features/odds-page/league-icon';
import { usePathname } from 'next/navigation';
import { Icon } from '@/components/icons/svg-icons';
import { useGoldenDuelSetting } from '@/hooks/query/golden-duel';
import { goldenDuelModalService } from '@/services/golden-duel/golden-duel-modal.service';
import { useThrottleClick } from '@nf/utils-common/hooks';
import { KafkaNote } from '@nf/utils-common/constants';
import { setKafkaNormal } from '@/hooks/query/use-kafka-log';

interface LeagueHeaderContainerProps {
	isLeagueExpand: boolean;
	setIsLeagueExpand: React.Dispatch<React.SetStateAction<boolean>>;
	sport: number;
	leagueId: number;
	leagueIdForMyFavourite: number;
	leagueGroupId: number;
	leagueName: string;
	parentMatchId?: number;
	matchsCount?: number;
}

export const LeagueHeader = ({
	isLeagueExpand,
	setIsLeagueExpand,
	sport,
	leagueId,
	leagueIdForMyFavourite,
	leagueGroupId,
	leagueName,
	parentMatchId,
	matchsCount,
}: LeagueHeaderContainerProps) => {
	const oddsPageLayout = useAppearanceStore((state) => state.oddsPageLayout);
	const { SiteConfigsData: siteConfig } = useGetSiteConfigs();
	const { isGoldenDuelSupported } = useGoldenDuelSetting();
	const pathname = usePathname();

	const leagueExpand = () => {
		setIsLeagueExpand(!isLeagueExpand)
	};

	const setKafka = () => {
		const kafkaData = {
			note: KafkaNote.GoldenDuel,
			type: 1,
			action: 10
		};
		setKafkaNormal(kafkaData);
	}

	const clickDetailIcon = useThrottleClick(() => {
		setKafka();
		goldenDuelModalService.displayGoldenDuelRulesModal(true);
	}, 300);

	const { user } = useAccountInfo();
	const hasOddsBoostPromo = (Boolean(user?.PromotionOddsBoostInfo?.EnablePromotionOddsBoost) && leagueId === 3) ||
		(Boolean(user?.PromotionOddsBoostInfo?.EnableAffPromotionOddsBoost) && leagueId === 155467);
	const isGoldenDuel = isGoldenDuelSupported(sport, leagueId);

	return (
		<div
			className={clsx('league-header', {
				[leagueStyles.oddsBoostPromo]: hasOddsBoostPromo,
				'has-market-bar': !pathname.match(/live/)
			})}
			onClick={leagueExpand}
			data-open={isLeagueExpand}
		>
			<MyFavouritesLeagueButton leagueId={leagueIdForMyFavourite} sportType={sport} />
			<LeagueIcon
				leagueId={leagueId}
				sport={sport}
				parentMatchId={parentMatchId}
				enableOddsBoostPromo={hasOddsBoostPromo}
				isGoldenDuel={isGoldenDuel}
				goldenDuelTag={oddsPageLayout === 'list' ? 'small' : 'normal'}
			/>
			<div className={leagueStyles.nameWrapper}>
				<div className={clsx('league-header__name', {
					[leagueStyles.nbaTag]: isGoldenDuel
				})}
				>
					{leagueName}
				</div>
				{isGoldenDuel ? (
					<button className={leagueStyles.detailWrapper} onClick={clickDetailIcon}>
						<Icon name="details" className={leagueStyles.nbaDetail} />
					</button>
				) : null}
			</div>
			{hasOddsBoostPromo && oddsPageLayout === 'list' ? (
				<img
					className={clsx('oddsboost-icon-list', leagueStyles.oddsBoostIcon)}
					src={siteService.combineImagePath('/images/promotion/odds-boost-promo/oddsboost-promo-icon.png', true, siteConfig)}
					alt="odds-boost-promo"
				/>
			) : null}
			{
				sport !== 999 &&
				(
					<>
						{oddsPageLayout === 'list' && <ListHeader sport={Number(sport)} />}
						<div className="league-header__option">
							<div className="league-header__amount">{matchsCount}</div>
						</div>
					</>
				)
			}
			{hasOddsBoostPromo && oddsPageLayout === 'card' ? (
				<img
					className={clsx('oddsboost-icon-card', leagueStyles.oddsBoostIcon)}
					src={siteService.combineImagePath('/images/promotion/odds-boost-promo/oddsboost-promo-icon.png', true, siteConfig)}
					alt="odds-boost-promo"
				/>
			) : null}
		</div>
	)
}